<script setup lang="ts">
import axios from "axios";
import { AdButlerGrid } from "@stockstotrade_com/stt-ts-shared-lib";

const $route = useRoute();
const $config = useRuntimeConfig();
const loadMoreAnchor = ref(null);
const showAds = ref(false);
const { fetchFallbackAdbutler } = useAdbutlerFallback();
const { show_adbutler } = $config.public;
const breakpoints = reactive(
	useBreakpoints({
		large: 1024
	})
);

const { data, status: blogStatus } = await useAsyncData("blog", async ({ $config }) => {
	const hostname = $config.public["hostname"];

	const [pageResult, categoriesResult, postsResult, featuredResult] =
		await Promise.allSettled([
			axios.get(`${hostname}wp-json/wp/v2/pages/3164`),
			axios.get(`${hostname}wp-json/wp/v2/categories?per_page=99`),
			axios.get(`${hostname}wp-json/wp/v2/posts?per_page=9&_embed`),
			$fetch("/api/featured-posts")
		]);

	const page =
		pageResult.status === "fulfilled" ? pageResult.value?.data : null;
	const categories =
		categoriesResult.status === "fulfilled"
			? categoriesResult.value?.data ?? []
			: [];
	const postsData =
		postsResult.status === "fulfilled" ? postsResult.value?.data ?? [] : [];
	const postsHeaders =
		postsResult.status === "fulfilled" ? postsResult.value?.headers ?? {} : {};
	const totalPages = parseInt(
		(postsHeaders as any)["x-wp-totalpages"] ?? "1",
		10
	);

	const featuredArticles: any[] = [];
	if (featuredResult.status === "fulfilled") {
		const { posts: fposts, screenerPage } = featuredResult.value as any;
		if (fposts) featuredArticles.push(...fposts);
		if (screenerPage) featuredArticles.push(screenerPage);
	}

	return { page, categories, posts: postsData, totalPages, featured_articles: featuredArticles };
});

const { page, categories } = data.value ?? { page: null, categories: [] };

// These depend on data from useAsyncData so must be declared after the await above.
const posts = ref<any[]>(data.value?.posts ?? []);
const featured_articles = computed(() => data.value?.featured_articles ?? []);
const isSearch = ref(false);
const totalPages = ref(data.value?.totalPages ?? 0);
const currentPage = ref(1);
const loadMore = ref(true);
const showLoad = ref((data.value?.posts ?? []).length === 0);

const featuredCategories = categories.filter((category) => {
	const namesToFilter = [
		"Trading News",
		"Announcements",
		"Bohens Take",
		"Chart of the Day",
		"Crypto",
		"Cryptocurrency"
	];
	return namesToFilter.includes(category.name);
});

const filterCategory = (id) => {
	const category = categories.find((category) => category.id === id);

	return category;
};

async function initialPostFetch() {
	let data = null;
	let headers = null;
	if ($route.query && $route.query.search) {
		const response = await axios.get(
			$config.public["hostname"] +
				"wp-json/wp/v2/posts?search=" +
				$route.query.search
		);

		if (response.data) {
			const new_arr = await fetchMedia(response.data);

			isSearch.value = true;
			data = new_arr;
			headers = response.headers;
		}
	} else {
		const response = await axios.get(
			$config.public["hostname"] + "wp-json/wp/v2/posts?per_page=9&_embed"
		);

		if (response.data) {
			data = response.data;
			headers = response.headers;
		}
	}

	posts.value = data;
	showLoad.value = false;
	totalPages.value = headers["x-wp-totalpages"];
	((totalPages.value = headers["x-wp-totalpages"]), (currentPage.value = 1));
	loadMore.value = true;
}

async function fetchMorePosts() {
	let post_list = [];

	if (currentPage.value > totalPages.value) {
		loadMore.value = false;
		return;
	}

	currentPage.value += 1;

	if ($route.query && $route.query.search) {
		const { data } = await axios.get(
			$config.public["hostname"] +
				"wp-json/wp/v2/posts?search=" +
				$route.query.search +
				"&page=" +
				currentPage.value
		);

		const new_arr = await fetchMedia(data);

		post_list = new_arr;
	} else {
		const { data } = await axios.get(
			$config.public["hostname"] +
				"wp-json/wp/v2/posts?per_page=9&page=" +
				currentPage.value +
				"&_embed"
		);

		post_list = data;
	}

	if (post_list.length) {
		posts.value = [...posts.value, ...post_list];
		loadMore.value = true;
		showLoad.value = false;
	}
}
async function fetchMedia(data) {
	let new_arr = data.map(async (item) => {
		const arr = item;

		if (item.featured_media) {
			const { data } = await axios.get(
				$config.public["hostname"] + "wp-json/wp/v2/media/" + item.featured_media
			);

			arr.featured_media = data;
		}
		return { ...arr };
	});

	new_arr = await Promise.all([...new_arr]);
	return new_arr;
}

const highlightedPosts = computed(() => {
	if (posts.value && posts.value.length > 0) return posts.value;

	return [];
});

const isElementInViewport = (element) => {
	if (!element) return false;
	const rect = element.getBoundingClientRect();
	return (
		rect.top >= 0 &&
		rect.left >= 0 &&
		rect.bottom <=
			(window.innerHeight || document.documentElement.clientHeight) &&
		rect.right <= (window.innerWidth || document.documentElement.clientWidth)
	);
};

const handleVisibilityChange = () => {
	const visibilityCheck = isElementInViewport(loadMoreAnchor.value);
	if (
		visibilityCheck &&
		loadMore.value &&
		currentPage.value !== totalPages.value
	) {
		showLoad.value = true;
		loadMore.value = false;
		fetchMorePosts();
	}
};

onMounted(async () => {
	window.addEventListener("scroll", handleVisibilityChange);

	// Skip initial post fetch if SSR already provided posts and there's no search query.
	// For search queries always refetch because SSR doesn't handle them.
	if ($route.query.search || posts.value.length === 0) {
		initialPostFetch();
	}

	if (page?.yoast_meta?.[15]?.content) {
		const scriptElement = document.createElement("script");
		scriptElement.innerHTML = page.yoast_meta[15].content;
		scriptElement.classList.add(`saswp-schema-markup-output`);
		scriptElement.type = "application/ld+json";
		document.head.appendChild(scriptElement);
	}

	if (import.meta.client) {
		showAds.value = window.adsAreWithUs;

		// Featured articles are now fetched server-side in useAsyncData.
		// Only fall back to client-side fetch if SSR returned nothing.
		if (featured_articles.value.length === 0) {
			try {
				const { posts: fposts, screenerPage } = await $fetch("/api/featured-posts") as any;
				const clientPosts: any[] = [];
				if (fposts) clientPosts.push(...fposts);
				if (screenerPage) clientPosts.push(screenerPage);

				if (clientPosts.length > 0 && data.value) {
					data.value = { ...data.value, featured_articles: clientPosts };
				}
			} catch (err) {
				console.warn("[blog] Failed to fetch featured posts:", err);
			}
		}

		if (!window.adsAreWithUs) {
			const banner = await fetchFallbackAdbutler("stt-grid-ad");

			if (banner) {
				const gridAdContainer = document.querySelector(
					".ad-buttler-ribbon-container"
				);
				if (gridAdContainer) {
					gridAdContainer.innerHTML = banner;
				}
			}
		}
	}
});

onUnmounted(() => {
	window.removeEventListener("scroll", handleVisibilityChange);
});

watch(
	() => $route.fullPath,
	(to, from) => {
		if (from.fullPath != to.fullPath) initialPostFetch();
	}
);

const firstPost = computed(() => {
	if (posts.value && posts.value.length > 0) {
		return posts.value[0];
	} else {
		return null;
	}
});
</script>

<template>
	<Head v-if="page.yoast_meta">
		<Title>StocksToTrade | Blog</Title>
		<Meta
			name="description"
			content="StocksToTrade have built-in proprietary algorithms and scanners to quickly uncover big potential opportunities happening at this moment."
		/>
		<Meta
			name="og:description"
			content="StocksToTrade have built-in proprietary algorithms and scanners to quickly uncover big potential opportunities happening at this moment."
		/>

		<Link
			:href="$config.public.current_hostname.slice(0, -1) + '/blog/'"
			rel="canonical"
		/>
		<Meta
			:content="$config.public.current_hostname.slice(0, -1) + '/blog/'"
			property="og:url"
		/>

		<template
			v-for="(tag, i) in page.yoast_meta"
			:key="i"
		>
			<template
			v-if="
					tag.content &&
					!tag.content.startsWith('[vc_row') &&
					tag.name !== 'canonical' &&
					tag.name !== 'title' &&
					tag.name !== 'robots' &&
					tag.content !== 'Array'
				"
			>
				<Meta
					:property="tag.property"
					:content="tag.content"
					v-if="tag.property && tag.property !== 'og:url'"
				/>
				<Meta
					:name="tag.name && tag.name !== 'Array'"
					:content="tag.content"
					v-else-if="tag.name"
				/>
			</template>
		</template>
	</Head>

	<template v-if="!isSearch">
		<BlogHeroSection
			:post="firstPost"
			:category="
				posts.length > 0 && posts[0] ? filterCategory(posts[0].categories[0]) : null
			"
		/>

		<BlogSlider
			:categories="categories"
			:posts="featured_articles"
			:loaded="blogStatus !== 'pending'"
			v-if="breakpoints.isGreaterOrEqual('large')"
		/>

		<div
			v-else
			class="flex flex-col gap-[24px] px-[16px] pb-[32px]"
		>
			<h2 class="title text-center text-[24px] lg:text-[36px]">
				Featured Articles
			</h2>

			<BlogCard
				v-if="posts && featured_articles.length > 0"
				:key="i"
				v-bind="post"
				:category="
					post.categories && post.categories.length > 0 ? post.categories[0] : null
				"
				v-for="(post, i) in featured_articles.slice(0, 4)"
			/>
			<template v-else>
				<SkeletonBlogCard />
				<SkeletonBlogCard />
				<SkeletonBlogCard />
				<SkeletonBlogCard />
			</template>
		</div>
		<BlogHeader
			title="Browse by Categories"
			:categories="featuredCategories"
			description="Select a category to see more related content"
		/>
	</template>

	<Row
		tag="section"
		class="bg-black"
	>
		<Container>
			<template v-if="isSearch">
				<Heading
					:text="`Search results for <b>${$route.query.search}</b>`"
					tag="h2"
					class="mb-[64px]"
				/>
			</template>

			<Heading
				v-else
				type="primary"
				text="Popular Articles"
				tag="h2"
				class="text-center mb-[64px]"
			/>

			<div
				class="blog-articles mb-[32px] lg:mb-[64px]"
				v-if="posts.length > 0"
			>
				<template
					v-for="(post, i) in posts"
					:key="i"
				>
					<BlogCard
						v-bind="post"
						:category="filterCategory(post.categories[0])"
					/>

					<AdButlerGrid
						class="adbutler-grid"
						:ad-id="741014"
						v-if="(i + 1) % 5 === 0 && showAds && show_adbutler"
					/>
					<div
						v-else-if="!showAds && show_adbutler && (i + 1) % 5 === 0"
						class="adbutler-grid-ad"
					></div>
				</template>
				<!-- <BlogCard
					:key="i"
					v-bind="post"
					:category="filterCategory(post.categories[0])"
					v-for="(post, i) in posts"
				/> -->
			</div>
			<div
				class="blog-articles mb-[32px] lg:mb-[64px]"
				v-else
			>
				<SkeletonBlogCard />
				<SkeletonBlogCard />
				<SkeletonBlogCard />
			</div>

			<div
				v-if="posts.length"
				ref="loadMoreAnchor"
				class="post-loading mx-auto"
			>
				<img
					v-if="showLoad"
					src="/images/loading.gif"
					class="loading-gif mx-auto mt-[32px]"
				/>
			</div>
		</Container>
	</Row>

	<SttOnYoutube id="blog-stt-on-youtube" />

	<Row>
		<Container class="flex flex-col lg:flex-row gap-[36px]">
			<NuxtLink
				v-for="(post, i) in posts.slice(0, 2)"
				:key="i"
				:href="`/${post.slug}/`"
				class="items-stretch shadow-2xl bg-white flex w-full overflow-hidden flex-col pb-3.5 rounded-2xl max-md:max-w-full"
			>
				<img
					width="606"
					height="332"
					loading="lazy"
					:src="
						post._embedded &&
						post._embedded['wp:featuredmedia'] &&
						post._embedded['wp:featuredmedia'][0].source_url
							? post._embedded['wp:featuredmedia'][0].source_url
							: '/images/block-ready-bg.jpg'
					"
					class="object-contain object-center w-full overflow-hidden max-md:max-w-full"
				/>
				<div
					class="items-stretch flex w-full flex-col mt-6 px-[16px] lg:px-[32px] max-md:max-w-full"
				>
					<h1
						class="overflow-hidden text-blue-500 text-ellipsis text-sm font-bold leading-5 w-full mb-[8px]"
					>
						{{ filterCategory(post.categories[0]).name }}
					</h1>
					<h2
						class="overflow-hidden text-black text-ellipsis mb-[16px] lg:mb-[24px] text-[22px] lg:text-4xl font-medium leading-tight tracking-tighter"
					>
						{{ post.title.rendered }}
					</h2>
					<div class="items-center inline-flex w-full justify-between">
						<div class="items-stretch flex justify-between gap-2">
							<img
								loading="lazy"
								:src="
									post._embedded && post._embedded['author']
										? post._embedded['author'][0].avatar_urls['48']
										: 'https://secure.gravatar.com/avatar/ffab25dfa1b032865bcfe0a47dde563a?s=48&d=mm&r=g'
								"
								class="aspect-square object-contain object-center w-[32px] overflow-hidden shrink-0 rounded-[48px]"
							/>
							<p
								class="text-black text-sm font-medium leading-5 self-center grow whitespace-nowrap my-auto"
							>
								Tim Bohen
							</p>
						</div>
						<div class="items-center flex gap-2">
							<img
								loading="lazy"
								src="https://cdn.builder.io/api/v1/image/assets/TEMP/e1056984227e90b51177cff1cd6e9ce5440a12f9f668cbb2f111d53005f54743?apiKey=da983b91ec1d46989670bd2839624a11&"
								class="aspect-square object-contain object-center w-[24px] lg:w-[32px] overflow-hidden shrink-0"
							/>
							<p
								class="text-black text-sm font-medium leading-5 self-center grow whitespace-nowrap my-auto"
							>
								{{ calculateReadingTime(post.content.rendered) }} min read
							</p>
						</div>
					</div>
				</div>
			</NuxtLink>
		</Container>
	</Row>
</template>

<style lang="scss">
@use "~/assets/utils.scss" as *;

.adbutler-grid {
	overflow: hidden;
	border-radius: 12px;
	img {
		@include breakpoint(md) {
			min-height: 306px;
			object-fit: cover;
		}
	}

	.post-metadata {
		color: $blue-500;
		font-weight: bold;
		a {
			display: flex;
			justify-content: space-between;
		}
	}

	h3 {
		color: $slate-900;
	}

	div.bg-white {
		height: 100% !important;
	}

	.plc741014,
	div:first-child {
		height: 100%;
	}
}
</style>
